import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/commandes-vn';
import './CommandesVN.css';

// ═══════════════════════════════════════════════════════════════════════════
// Commandes VN — remplace le fichier Excel de suivi des commandes VN.
//   · Couverture : une ligne par version ; ventes nettes et stock viennent du
//     DMS (A97 Data), les commandes de cette page. Le calcul est côté serveur
//     (routes/commandes-vn.js → versionner()) : l'écran n'additionne rien qu'il
//     ne reçoive déjà.
//   · Commandes : dossiers → lots (quantité, ETA, n° constructeur, paiement).
//   · Calendrier : arrivées et décaissements prévus, mois par mois.
//   · Paramètres : délais, versions suivies, correspondance des libellés DMS.
// ═══════════════════════════════════════════════════════════════════════════

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisLib = (ym) => { const [a, m] = ym.split('-'); return `${MOIS_COURTS[Number(m) - 1]} ${a.slice(2)}`; };
const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nf.format(Math.round(Number(v)))} €`);
const keur = (v) => (v == null ? '—' : Math.abs(v) >= 1e6 ? `${(v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} M€` : `${nf.format(Math.round(v / 1000))} k€`);
const dateFr = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '—');
const isoJour = (v) => (v ? String(v).slice(0, 10) : '');
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const ETATS = {
  rupture: { label: 'Rupture', classe: 'badge-pill badge-error', aide: 'Le stock, plus ce qui arrive avant le délai de commande, ne couvre pas les ventes prévues sur ce délai.' },
  a_commander: { label: 'À commander', classe: 'badge-pill badge-warning', aide: 'Les ressources (stock + commandes) ne couvrent pas le besoin sur le délai de commande + la couverture voulue.' },
  ok: { label: 'Couvert', classe: 'badge-pill badge-success', aide: 'Les ressources couvrent le besoin.' },
  surstock: { label: 'Surstock', classe: 'badge-pill badge-neutral', aide: 'Plus de 3 mois au-delà de la couverture voulue.' },
  inactif: { label: '—', classe: 'text-muted', aide: 'Ni stock, ni vente sur 12 mois, ni commande, ni besoin prévu.' },
};
const STATUTS = [
  { code: 'production', label: 'En production' },
  { code: 'flottant', label: 'Flottant' },
  { code: 'livre', label: 'Livré' },
];

function erreurDe(e) { return e?.response?.data?.error || e?.message || 'Erreur'; }

const ONGLETS = [['couverture', '📊 Couverture'], ['commandes', '📦 Commandes'], ['calendrier', '🗓️ Calendrier'], ['parametres', '⚙️ Paramètres']];
// Filtre de la barre d'outils, propre à chaque onglet (le premier = défaut).
const FILTRES = {
  couverture: [['actives', 'Avec activité'], ['action', 'À traiter'], ['tout', 'Toutes les versions']],
  commandes: [['en_cours', 'En cours'], ['terminees', 'Terminées'], ['tout', 'Toutes les commandes']],
  parametres: [['a_traiter', 'Libellés à traiter'], ['tout', 'Tous les libellés']],
};
const contient = (q, ...champs) => !q || champs.some((c) => String(c || '').toLowerCase().includes(q));

export default function CommandesVN({ user, setHeaderAction, editMode }) {
  const [perimetres, setPerimetres] = useState(null);
  const [concessionId, setConcessionId] = useState(() => Number(localStorage.getItem('cvn-concession')) || null);
  const [marque, setMarque] = useState(() => localStorage.getItem('cvn-marque') || '');
  const [onglet, setOnglet] = useState('couverture');
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState(FILTRES.couverture[0][0]);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    api.perimetres().then((r) => {
      const ps = r.data.perimetres || [];
      setPerimetres(ps);
      const p = ps.find((x) => x.concession_id === concessionId) || ps[0];
      if (p && p.concession_id !== concessionId) setConcessionId(p.concession_id);
      if (p && !p.marques.includes(marque)) setMarque(p.suivies[0] || p.marques[0]);
    }).catch((e) => setErreur(erreurDe(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { try { if (concessionId) localStorage.setItem('cvn-concession', String(concessionId)); if (marque) localStorage.setItem('cvn-marque', marque); } catch (e) { /* navigation privée */ } }, [concessionId, marque]);

  const perim = perimetres?.find((p) => p.concession_id === concessionId);
  const changerConcession = (id) => {
    const p = perimetres.find((x) => x.concession_id === Number(id));
    setConcessionId(p.concession_id);
    if (!p.marques.includes(marque)) setMarque(p.suivies[0] || p.marques[0]);
  };
  const allerA = (k) => { setOnglet(k); setFiltre(FILTRES[k]?.[0][0] || ''); setRecherche(''); };
  const q = recherche.trim().toLowerCase();
  const commun = { concessionId, marque, setHeaderAction, recherche: q, filtre };

  return (
    <div className="page-container">
      <div className="page-tabs">
        {ONGLETS.map(([k, l]) => <button key={k} className={onglet === k ? 'active' : ''} onClick={() => allerA(k)}>{l}</button>)}
      </div>

      <div className="page-toolbar">
        {onglet !== 'calendrier' && <input className="page-search" placeholder="🔍 Rechercher..." value={recherche} onChange={(e) => setRecherche(e.target.value)} />}
        {perimetres && perimetres.length > 1 && (
          <select className="page-select" value={concessionId || ''} onChange={(e) => changerConcession(e.target.value)}>
            {perimetres.map((p) => <option key={p.concession_id} value={p.concession_id}>{p.concession}</option>)}
          </select>
        )}
        {perim && (
          <select className="page-select" value={marque} onChange={(e) => setMarque(e.target.value)} title="Une marque « à paramétrer » n'a pas encore de versions de suivi">
            {perim.marques.map((m) => <option key={m} value={m}>{m}{perim.suivies.includes(m) ? '' : ' (à paramétrer)'}</option>)}
          </select>
        )}
        {FILTRES[onglet] && (
          <select className={`page-select${filtre !== FILTRES[onglet][0][0] ? ' active-filter' : ''}`} value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            {FILTRES[onglet].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
      </div>

      {erreur && <div className="status-error cvn-bandeau">{erreur}</div>}
      {perimetres && perimetres.length === 0 && <div className="empty-state">Aucune concession avec des ventes ou du stock VN dans votre périmètre.</div>}
      {perimetres === null && !erreur && <div className="empty-state">Chargement…</div>}

      {concessionId && marque && onglet === 'couverture' && <Couverture {...commun} allerA={allerA} />}
      {concessionId && marque && onglet === 'commandes' && <Commandes {...commun} user={user} editMode={editMode} />}
      {concessionId && marque && onglet === 'calendrier' && <Calendrier {...commun} />}
      {concessionId && marque && onglet === 'parametres' && <Parametres {...commun} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COUVERTURE
// ═══════════════════════════════════════════════════════════════════════════
function Couverture({ concessionId, marque, allerA, recherche, filtre, setHeaderAction }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouverte, setOuverte] = useState(null);

  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);
  const charger = useCallback(() => {
    setErreur('');
    api.couverture(concessionId, marque).then((r) => setData(r.data)).catch((e) => { setErreur(erreurDe(e)); setData(null); });
  }, [concessionId, marque]);
  useEffect(() => { setData(null); setOuverte(null); charger(); }, [charger]);

  const groupes = useMemo(() => {
    if (!data) return [];
    const g = new Map();
    for (const v of data.versions.filter((x) => x.actif)) {
      if (!g.has(v.modele)) g.set(v.modele, []);
      g.get(v.modele).push(v);
    }
    return [...g.entries()];
  }, [data]);

  if (erreur) return <div className="status-error cvn-bandeau">{erreur}</div>;
  if (!data) return <div className="empty-state">Chargement de la couverture…</div>;
  if (!data.versions.length) {
    return (
      <div className="empty-state">
        Aucune version de suivi pour {marque}.<br />
        <button type="button" className="btn-primary" style={{ marginTop: 12 }} onClick={() => allerA('parametres')}>Créer les versions</button>
      </div>
    );
  }

  const somme = (k, arr) => arr.reduce((s, v) => s + (Number(v[k]) || 0), 0);
  const actives = data.versions.filter((v) => v.actif);
  const nbRupture = actives.filter((v) => v.etat === 'rupture').length;
  const p = data.parametres;
  const visibles = (vs) => vs.filter((v) => contient(recherche, v.libelle, v.modele)
    && (filtre === 'tout' || (filtre === 'action' ? ['rupture', 'a_commander'].includes(v.etat) : v.etat !== 'inactif')));
  const ventes3 = (v) => v.ventes.slice(-4, -1); // 3 derniers mois clos
  const aClasser = data.a_classer.filter((a) => a.ventes_24m + a.stock > 0);

  return (
    <>
      <div className="cvn-kpis">
        <div className="cvn-kpi"><span className="l">Stock VN</span><span className="v">{nf.format(somme('stock', actives))}</span><span className="d">+ {somme('stock_vd', actives)} VD · DMS, la veille</span></div>
        <div className="cvn-kpi"><span className="l">En production</span><span className="v">{nf.format(somme('en_production', actives))}</span><span className="d">commandés, non expédiés</span></div>
        <div className="cvn-kpi"><span className="l">Flottant</span><span className="v">{nf.format(somme('flottant', actives))}</span><span className="d">expédiés, en route</span></div>
        <div className={`cvn-kpi${somme('a_commander', actives) > 0 ? ' cvn-kpi--alerte' : ''}`}><span className="l">À commander</span><span className="v">{nf.format(somme('a_commander', actives))}</span><span className="d">{nbRupture} version{nbRupture > 1 ? 's' : ''} en rupture</span></div>
      </div>

      <p className="cvn-regle">
        Délai de commande <b>{String(p.delai_commande_mois).replace('.', ',')} mois</b> · couverture voulue <b>{String(p.couverture_cible_mois).replace('.', ',')} mois</b> :
        on compare les ventes prévues sur <b>{Math.ceil(p.delai_commande_mois + p.couverture_cible_mois)} mois</b> au stock + commandes en cours.
        {!p.configure && <> <button type="button" className="cvn-lien" onClick={() => allerA('parametres')}>Régler les délais de {marque}</button></>}
      </p>

      {(aClasser.length > 0 || data.correspondances_proposees > 0 || data.alertes.eta_depassees > 0) && (
        <div className="cvn-alertes">
          {aClasser.length > 0 && (
            <button type="button" className="badge-pill badge-warning" onClick={() => allerA('parametres')}>⚠️ {aClasser.length} libellé{aClasser.length > 1 ? 's' : ''} du DMS non rattaché{aClasser.length > 1 ? 's' : ''} : {aClasser.reduce((s, a) => s + a.ventes_24m, 0)} ventes et {aClasser.reduce((s, a) => s + a.stock, 0)} véhicules hors tableau</button>
          )}
          {data.correspondances_proposees > 0 && (
            <button type="button" className="badge-pill badge-info" onClick={() => allerA('parametres')}>🔎 {data.correspondances_proposees} correspondance{data.correspondances_proposees > 1 ? 's' : ''} proposée{data.correspondances_proposees > 1 ? 's' : ''} automatiquement, à vérifier</button>
          )}
          {data.alertes.eta_depassees > 0 && (
            <button type="button" className="badge-pill badge-warning" onClick={() => allerA('commandes')}>⏰ {data.alertes.eta_depassees} lot{data.alertes.eta_depassees > 1 ? 's' : ''} dont l'ETA est dépassée</button>
          )}
        </div>
      )}

      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead>
            <tr>
              <th className="l">Version</th>
              <th title="Ventes nettes (factures − avoirs) des 3 derniers mois clos">Ventes 3 mois</th>
              <th title="Ventes moyennes mensuelles sur 6 mois">VMM 6</th>
              <th>Stock</th>
              <th>En prod.</th>
              <th>Flottant</th>
              <th title="(Stock + commandes en cours) / ventes prévues par mois">Couverture</th>
              <th title="Ventes prévues sur le délai de commande + la couverture voulue">Besoin</th>
              <th>À commander</th>
              <th>État</th>
            </tr>
          </thead>
          <tbody>
            {groupes.map(([modele, vs]) => {
              const affich = visibles(vs);
              if (!affich.length) return null;
              return (
                <React.Fragment key={modele}>
                  <tr className="cvn-groupe">
                    <td className="l">{modele}</td>
                    <td>{nf.format(vs.reduce((s, v) => s + ventes3(v).reduce((a, x) => a + x.n, 0), 0))}</td>
                    <td>{somme('vmm6', vs).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</td>
                    <td>{somme('stock', vs)}</td>
                    <td>{somme('en_production', vs)}</td>
                    <td>{somme('flottant', vs)}</td>
                    <td />
                    <td>{somme('besoin', vs)}</td>
                    <td className={somme('a_commander', vs) > 0 ? 'cvn-fort' : ''}>{somme('a_commander', vs) || '—'}</td>
                    <td />
                  </tr>
                  {affich.map((v) => (
                    <React.Fragment key={v.id}>
                      <tr className={`cvn-ligne${ouverte === v.id ? ' ouverte' : ''}`} onClick={() => setOuverte(ouverte === v.id ? null : v.id)}>
                        <td className="l"><span className="cvn-chevron">{ouverte === v.id ? '▾' : '▸'}</span>{v.libelle}{v.delai_propre && <span className="inline-tag inline-tag-muted" title="Délai de commande propre à cette version">{String(v.delai_commande_mois).replace('.', ',')} mois</span>}</td>
                        <td className="cvn-mini">{ventes3(v).map((x) => <span key={x.ym} title={moisLib(x.ym)}>{x.n}</span>)}</td>
                        <td>{v.vmm6.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</td>
                        <td>{v.stock}{v.stock_vd > 0 && <small className="cvn-vd"> +{v.stock_vd} VD</small>}</td>
                        <td>{v.en_production || '—'}</td>
                        <td>{v.flottant || '—'}</td>
                        <td>{v.couverture_mois == null ? '—' : `${v.couverture_mois.toLocaleString('fr-FR')} mois`}</td>
                        <td>{v.besoin}</td>
                        <td className={v.a_commander > 0 ? 'cvn-fort' : ''}>{v.a_commander || '—'}</td>
                        <td><span className={ETATS[v.etat].classe} title={ETATS[v.etat].aide}>{ETATS[v.etat].label}</span></td>
                      </tr>
                      {ouverte === v.id && (
                        <tr className="cvn-detail"><td colSpan={10}><DetailVersion v={v} onChange={charger} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="l">Total {marque}</td>
              <td>{nf.format(actives.reduce((s, v) => s + ventes3(v).reduce((a, x) => a + x.n, 0), 0))}</td>
              <td>{somme('vmm6', actives).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}</td>
              <td>{somme('stock', actives)}</td>
              <td>{somme('en_production', actives)}</td>
              <td>{somme('flottant', actives)}</td>
              <td />
              <td>{somme('besoin', actives)}</td>
              <td>{somme('a_commander', actives)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// Détail d'une version : 24 mois de ventes + prévisions ajustables.
function DetailVersion({ v, onChange }) {
  const [enCours, setEnCours] = useState(null);
  const max = Math.max(1, ...v.ventes.map((x) => x.n), ...v.previsions.map((x) => x.quantite));
  const ajuster = async (ym, valeur) => {
    setEnCours(ym);
    try { await api.ajusterPrevision(v.id, ym, valeur === '' ? null : Number(valeur)); onChange(); }
    catch (e) { alert(erreurDe(e)); } finally { setEnCours(null); }
  };
  return (
    <div className="cvn-detail-corps">
      <div className="cvn-histo" aria-label="Ventes nettes des 24 derniers mois puis prévisions">
        {v.ventes.map((x, i) => (
          <div key={x.ym} className={`cvn-barre${i === v.ventes.length - 1 ? ' encours' : ''}`} title={`${moisLib(x.ym)} : ${x.n} vente${x.n > 1 ? 's' : ''} nette${x.n > 1 ? 's' : ''}${i === v.ventes.length - 1 ? ' (mois en cours)' : ''}`}>
            <span style={{ height: `${Math.max(0, x.n) / max * 100}%` }} />
            <em>{x.n || ''}</em>
          </div>
        ))}
        {v.previsions.map((x) => (
          <div key={x.ym} className={`cvn-barre prevision${x.ajustee ? ' ajustee' : ''}`} title={`${moisLib(x.ym)} : ${x.quantite} prévu${x.ajustee ? ` (ajusté, auto ${x.auto})` : ''}`}>
            <span style={{ height: `${x.quantite / max * 100}%` }} />
            <em>{x.quantite || ''}</em>
          </div>
        ))}
      </div>
      <div className="cvn-histo-legende"><span className="cvn-l-reel" /> ventes nettes (24 mois) <span className="cvn-l-prev" /> prévision automatique <span className="cvn-l-ajust" /> prévision ajustée</div>
      <div className="cvn-previ">
        <div className="cvn-previ-titre">
          Prévisions — moyenne entre le même mois l'an dernier et le rythme des 6 derniers mois ({v.vmm6.toLocaleString('fr-FR')} / mois). Modifiez un mois pour l'ajuster ; videz-le pour revenir à l'automatique.
        </div>
        <div className="cvn-previ-grille">
          {v.previsions.map((x, i) => (
            <label key={x.ym} className={`${x.ajustee ? 'ajustee' : ''}${i < v.horizon_besoin_mois ? ' horizon' : ''}`} title={i < v.horizon_besoin_mois ? 'Compté dans le besoin' : 'Au-delà de l\'horizon du besoin'}>
              <span>{moisLib(x.ym)}</span>
              <input type="number" min="0" defaultValue={x.ajustee ? x.quantite : ''} placeholder={String(x.auto)} disabled={enCours === x.ym}
                onBlur={(e) => { const val = e.target.value; if ((val === '' && x.ajustee) || (val !== '' && Number(val) !== x.quantite)) ajuster(x.ym, val); }} />
            </label>
          ))}
        </div>
        <div className="cvn-previ-pied">
          Besoin sur {v.horizon_besoin_mois} mois : <b>{v.besoin}</b> · ressources : <b>{v.ressources}</b> (stock {v.stock} + en production {v.en_production} + flottant {v.flottant})
          {' '}· stock projeté à {Math.ceil(v.delai_commande_mois)} mois : <b className={v.stock_projete < 0 ? 'cvn-negatif' : ''}>{v.stock_projete}</b>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMMANDES
// ═══════════════════════════════════════════════════════════════════════════
function Commandes({ concessionId, marque, user, editMode, recherche, filtre, setHeaderAction }) {
  const [liste, setListe] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const [nouvelle, setNouvelle] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [erreur, setErreur] = useState('');
  const peutSupprimer = editMode && user?.role === 'super-admin';

  useEffect(() => {
    if (setHeaderAction) setHeaderAction({ primary: { label: '+ Nouvelle commande', onClick: () => setNouvelle({ reference: '', fournisseur: '', date_commande: aujourdhui() }) } });
  }, [setHeaderAction]);
  const charger = useCallback(() => {
    api.commandes(concessionId, marque).then((r) => setListe(r.data.commandes)).catch((e) => setErreur(erreurDe(e)));
  }, [concessionId, marque]);
  useEffect(() => { setListe(null); setOuverte(null); setSelection(new Set()); charger(); }, [charger]);
  useEffect(() => { if (!peutSupprimer) setSelection(new Set()); }, [peutSupprimer]);

  const creer = async () => {
    try {
      const { data } = await api.creerCommande(concessionId, marque, nouvelle);
      setNouvelle(null); charger(); setOuverte(data.commande.id);
    } catch (e) { alert(erreurDe(e)); }
  };
  const supprimer = async (ids) => {
    const cmds = (liste || []).filter((c) => ids.includes(c.id));
    if (!window.confirm(ids.length === 1 ? `Supprimer la commande ${cmds[0]?.reference} et tous ses lots ?` : `Supprimer ${ids.length} commandes et tous leurs lots ?`)) return;
    try { for (const id of ids) await api.supprimerCommande(id); } catch (e) { alert(erreurDe(e)); }
    setSelection(new Set()); charger();
  };

  const termine = (c) => c.quantite > 0 && c.livres >= c.quantite;
  const affich = (liste || []).filter((c) => contient(recherche, c.reference, c.fournisseur)
    && (filtre === 'tout' || (filtre === 'terminees' ? termine(c) : !termine(c))));
  const basculer = (id) => setSelection((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tousCoches = affich.length > 0 && affich.every((c) => selection.has(c.id));

  return (
    <>
      {erreur && <div className="status-error cvn-bandeau">{erreur}</div>}
      {peutSupprimer && selection.size > 0 && (
        <div className="page-bulk-bar">
          <span>{selection.size} sélectionnée{selection.size > 1 ? 's' : ''}</span>
          <button type="button" className="btn-secondary" onClick={() => supprimer([...selection])}>🗑️ Supprimer la sélection</button>
        </div>
      )}
      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead>
            <tr>
              {peutSupprimer && <th className="l"><input type="checkbox" checked={tousCoches} onChange={() => setSelection(tousCoches ? new Set() : new Set(affich.map((c) => c.id)))} /></th>}
              <th className="l">Référence</th>
              <th className="l">Fournisseur</th>
              <th>Commandée le</th>
              <th className="l">Livrés</th>
              <th>Flottant</th>
              <th>Prochaine ETA</th>
              <th>Montant HT</th>
              <th>Reste à payer</th>
              <th>Prochain paiement</th>
              <th className="l">Actions</th>
            </tr>
          </thead>
          <tbody>
            {liste === null && <tr><td colSpan={11} className="empty-state">Chargement…</td></tr>}
            {liste && affich.length === 0 && <tr><td colSpan={11} className="empty-state">{liste.length ? 'Aucune commande ne correspond aux filtres.' : `Aucune commande pour ${marque}.`}</td></tr>}
            {affich.map((c) => {
              const pct = c.quantite ? Math.round((c.livres / c.quantite) * 100) : 0;
              return (
                <tr key={c.id} className={`cvn-ligne${termine(c) ? ' cvn-livre' : ''}`} onClick={() => setOuverte(c.id)}>
                  {peutSupprimer && <td className="l" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selection.has(c.id)} onChange={() => basculer(c.id)} /></td>}
                  <td className="l"><b>{c.reference}</b></td>
                  <td className="l">{c.fournisseur || '—'}</td>
                  <td>{dateFr(c.date_commande)}</td>
                  <td className="l"><div className="cvn-livres"><div className="cvn-progression"><span style={{ width: `${pct}%` }} /></div><span>{c.livres}/{c.quantite}</span></div></td>
                  <td>{c.flottant || '—'}</td>
                  <td>{c.eta_depassees > 0 && <span className="badge-pill badge-warning cvn-badge-avant" title={`${c.eta_depassees} lot${c.eta_depassees > 1 ? 's' : ''} dont l'ETA est passée : sont-ils arrivés ?`}>⏰ {c.eta_depassees}</span>}{!termine(c) && c.prochaine_eta ? dateFr(c.prochaine_eta) : '—'}</td>
                  <td>{keur(Number(c.montant_ht))}</td>
                  <td className={Number(c.reste_a_payer) > 0 ? 'cvn-fort' : ''}>{Number(c.reste_a_payer) > 0 ? keur(Number(c.reste_a_payer)) : '—'}</td>
                  <td>{Number(c.reste_a_payer) > 0 && c.prochain_paiement ? dateFr(c.prochain_paiement) : '—'}</td>
                  <td className="l" onClick={(e) => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button type="button" className="btn-icon" title="Ouvrir" onClick={() => setOuverte(c.id)}>👁️</button>
                      {peutSupprimer && <button type="button" className="btn-icon delete" title="Supprimer" onClick={() => supprimer([c.id])}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {nouvelle && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle commande {marque}</h2>
              <button type="button" className="modal-close" onClick={() => setNouvelle(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full"><label>Référence *</label><input value={nouvelle.reference} autoFocus onChange={(e) => setNouvelle({ ...nouvelle, reference: e.target.value })} placeholder="Commande XYZ" /></div>
                <div className="form-group"><label>Fournisseur</label><input value={nouvelle.fournisseur} onChange={(e) => setNouvelle({ ...nouvelle, fournisseur: e.target.value })} placeholder="ex. ITC / Inchcape" /></div>
                <div className="form-group"><label>Date de commande</label><input type="date" value={nouvelle.date_commande} onChange={(e) => setNouvelle({ ...nouvelle, date_commande: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setNouvelle(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!nouvelle.reference.trim()} onClick={creer}>Créer</button>
            </div>
          </div>
        </div>
      )}
      {ouverte && <DossierCommande id={ouverte} fermer={() => { setOuverte(null); charger(); }} concessionId={concessionId} marque={marque} peutSupprimer={peutSupprimer} />}
    </>
  );
}

// Dossier d'une commande (modale) : en-tête modifiable + lots éditables en place.
function DossierCommande({ id, fermer, concessionId, marque, peutSupprimer }) {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [ajout, setAjout] = useState(null);
  const [entete, setEntete] = useState(null);
  const [frac, setFrac] = useState(null);

  const charger = useCallback(() => {
    api.commande(id).then((r) => setData(r.data)).catch((e) => alert(erreurDe(e)));
  }, [id]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    api.couverture(concessionId, marque).then((r) => setVersions(r.data.versions.filter((v) => v.actif))).catch(() => setVersions([]));
  }, [concessionId, marque]);

  const commande = data?.commande;
  const lots = data?.lots || [];
  const total = lots.reduce((s, l) => s + l.quantite, 0);
  const livres = lots.reduce((s, l) => s + (l.statut === 'livre' ? l.quantite : Math.min(l.quantite, l.livres_constates)), 0);
  const montant = lots.reduce((s, l) => s + Number(l.montant_ht || 0), 0);
  const reste = lots.filter((l) => !l.paye).reduce((s, l) => s + Number(l.montant_ht || 0), 0);

  const maj = async (lot, champs) => {
    try { await api.modifierLot(lot.id, champs); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const fractionner = async () => {
    try { await api.fractionnerLot(frac.lot.id, Number(frac.quantite), frac.eta || null); setFrac(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const supprimerLot = async (lot) => {
    if (!window.confirm(`Supprimer le lot ${lot.quantite} × ${lot.version} ?`)) return;
    try { await api.supprimerLot(lot.id); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const ajouter = async () => {
    try { await api.ajouterLot(id, ajout); setAjout(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const enregistrerEntete = async () => {
    try { await api.modifierCommande(id, entete); setEntete(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const supprimer = async () => {
    if (!window.confirm(`Supprimer la commande ${commande.reference} et ses ${lots.length} lots ?`)) return;
    try { await api.supprimerCommande(id); fermer(); } catch (e) { alert(erreurDe(e)); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large cvn-dossier" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📦 {commande ? commande.reference : 'Commande'}</h2>
          <button type="button" className="modal-close" onClick={fermer}>✕</button>
        </div>
        <div className="modal-body">
          {!data ? <div className="empty-state">Chargement…</div> : (
            <>
              <div className="cvn-dossier-h">
                {!entete ? (
                  <div>
                    <div className="cvn-dossier-meta">
                      <span>{commande.fournisseur || 'Fournisseur non renseigné'}</span>
                      {commande.date_commande && <span>commandée le {dateFr(commande.date_commande)}</span>}
                      <button type="button" className="btn-icon" title="Modifier" onClick={() => setEntete({ reference: commande.reference, fournisseur: commande.fournisseur || '', date_commande: isoJour(commande.date_commande), commentaire: commande.commentaire || '' })}>✏️</button>
                    </div>
                    {commande.commentaire && <p className="cvn-dossier-com">{commande.commentaire}</p>}
                  </div>
                ) : (
                  <div className="form-grid cvn-entete-form">
                    <div className="form-group"><label>Référence</label><input value={entete.reference} onChange={(e) => setEntete({ ...entete, reference: e.target.value })} /></div>
                    <div className="form-group"><label>Fournisseur</label><input value={entete.fournisseur} onChange={(e) => setEntete({ ...entete, fournisseur: e.target.value })} /></div>
                    <div className="form-group"><label>Date de commande</label><input type="date" value={entete.date_commande} onChange={(e) => setEntete({ ...entete, date_commande: e.target.value })} /></div>
                    <div className="form-group full"><label>Commentaire</label><textarea rows={2} value={entete.commentaire} onChange={(e) => setEntete({ ...entete, commentaire: e.target.value })} /></div>
                    <div className="form-group full cvn-form-actions">
                      <button type="button" className="btn-secondary" onClick={() => setEntete(null)}>Annuler</button>
                      <button type="button" className="btn-primary" onClick={enregistrerEntete}>Enregistrer</button>
                    </div>
                  </div>
                )}
                <div className="cvn-dossier-kpis">
                  <div><b>{livres}<span className="cvn-sur">/{total}</span></b><small>livrés</small></div>
                  <div><b>{keur(montant)}</b><small>montant HT</small></div>
                  <div><b>{keur(reste)}</b><small>reste à payer</small></div>
                </div>
              </div>

              <div className="table-container cvn-scroll">
                <table className="cvn-table cvn-lots">
                  <thead>
                    <tr>
                      <th className="l">Version</th><th>Qté</th><th className="l">Statut</th><th>ETA</th>
                      <th title="N° de commande du constructeur : quand les véhicules arrivent dans le DMS avec ce numéro, le lot passe seul en Livré">N° constructeur</th>
                      <th>Prix unit. HT</th><th>Montant HT</th>
                      <th title="ETA + délai de paiement de la marque, tant qu'elle n'est pas saisie à la main">Paiement prévu</th>
                      <th className="l">Payé</th><th className="l">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.map((l) => {
                      const depassee = l.statut !== 'livre' && l.eta && isoJour(l.eta) < aujourdhui();
                      return (
                        <tr key={l.id} className={l.statut === 'livre' ? 'cvn-livre' : ''}>
                          <td className="l">{l.version}{l.livres_constates > 0 && l.statut !== 'livre' && <span className="inline-tag inline-tag-muted" title="Véhicules arrivés dans le DMS avec ce n° de commande">{l.livres_constates} arrivé{l.livres_constates > 1 ? 's' : ''}</span>}</td>
                          <td><input className="cvn-num" type="number" min="1" defaultValue={l.quantite} onBlur={(e) => Number(e.target.value) !== l.quantite && maj(l, { quantite: Number(e.target.value) })} /></td>
                          <td className="l">
                            <select value={l.statut} onChange={(e) => maj(l, { statut: e.target.value })}>
                              {STATUTS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                            </select>
                          </td>
                          <td className={depassee ? 'cvn-depasse' : ''}><input type="date" defaultValue={isoJour(l.eta)} onBlur={(e) => e.target.value !== isoJour(l.eta) && maj(l, { eta: e.target.value || null })} title={depassee ? 'ETA dépassée : le lot est-il arrivé ?' : ''} /></td>
                          <td><input className="cvn-ref" defaultValue={l.ref_constructeur || ''} placeholder="à saisir" onBlur={(e) => e.target.value.trim() !== (l.ref_constructeur || '') && maj(l, { ref_constructeur: e.target.value })} /></td>
                          <td><input className="cvn-prix" type="number" min="0" step="100" defaultValue={l.prix_unitaire_ht ?? ''} onBlur={(e) => String(e.target.value) !== String(l.prix_unitaire_ht ?? '') && maj(l, { prix_unitaire_ht: e.target.value })} /></td>
                          <td className="cvn-fort">{eur(l.montant_ht)}</td>
                          <td><input type="date" defaultValue={isoJour(l.date_paiement_prevue)} className={l.paiement_manuel ? 'manuel' : ''} title={l.paiement_manuel ? 'Saisie à la main (videz pour revenir au calcul)' : 'Calculée : ETA + délai de paiement'} onBlur={(e) => e.target.value !== isoJour(l.date_paiement_prevue) && maj(l, { date_paiement_prevue: e.target.value || null })} /></td>
                          <td className="l"><label className="cvn-paye"><input type="checkbox" checked={l.paye} onChange={(e) => maj(l, { paye: e.target.checked })} />{l.paye && <small>{dateFr(l.date_paiement)}</small>}</label></td>
                          <td className="l">
                            <div className="action-buttons">
                              {l.quantite > 1 && <button type="button" className="btn-icon" title="Fractionner : une partie arrive à une autre date" onClick={() => setFrac({ lot: l, quantite: Math.floor(l.quantite / 2), eta: isoJour(l.eta) })}>✂️</button>}
                              {peutSupprimer && <button type="button" className="btn-icon delete" title="Supprimer" onClick={() => supprimerLot(l)}>🗑️</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {lots.length === 0 && <tr><td colSpan={10} className="empty-state">Aucun lot : ajoutez les versions commandées.</td></tr>}
                  </tbody>
                </table>
              </div>

              {!ajout ? (
                <button type="button" className="btn-secondary cvn-ajout" onClick={() => setAjout({ version_id: versions[0]?.id || '', quantite: 1, statut: 'production', eta: '' })}>+ Ajouter une version</button>
              ) : (
                <div className="form-grid cvn-ajout-form">
                  <div className="form-group full"><label>Version</label>
                    <select value={ajout.version_id} onChange={(e) => setAjout({ ...ajout, version_id: Number(e.target.value) })}>
                      {versions.map((v) => <option key={v.id} value={v.id}>{v.modele} · {v.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Quantité</label><input type="number" min="1" value={ajout.quantite} onChange={(e) => setAjout({ ...ajout, quantite: Number(e.target.value) })} /></div>
                  <div className="form-group"><label>Statut</label>
                    <select value={ajout.statut} onChange={(e) => setAjout({ ...ajout, statut: e.target.value })}>
                      {STATUTS.filter((s) => s.code !== 'livre').map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>ETA</label><input type="date" value={ajout.eta} onChange={(e) => setAjout({ ...ajout, eta: e.target.value })} /></div>
                  <div className="form-group cvn-form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setAjout(null)}>Annuler</button>
                    <button type="button" className="btn-primary" disabled={!ajout.version_id || !(ajout.quantite > 0)} onClick={ajouter}>Ajouter</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          {peutSupprimer && data && <button type="button" className="btn-secondary cvn-danger cvn-footer-gauche" onClick={supprimer}>🗑️ Supprimer la commande</button>}
          <button type="button" className="btn-secondary" onClick={fermer}>Fermer</button>
        </div>
      </div>

      {frac && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✂️ Fractionner le lot</h2>
              <button type="button" className="modal-close" onClick={() => setFrac(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="cvn-regle">{frac.lot.quantite} × {frac.lot.version} : une partie arrive à une autre date.</p>
              <div className="form-grid">
                <div className="form-group"><label>Véhicules déplacés</label><input type="number" min="1" max={frac.lot.quantite - 1} value={frac.quantite} onChange={(e) => setFrac({ ...frac, quantite: e.target.value })} /></div>
                <div className="form-group"><label>ETA de cette partie</label><input type="date" value={frac.eta} onChange={(e) => setFrac({ ...frac, eta: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setFrac(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!(Number(frac.quantite) > 0 && Number(frac.quantite) < frac.lot.quantite)} onClick={fractionner}>Fractionner</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CALENDRIER
// ═══════════════════════════════════════════════════════════════════════════
function Calendrier({ concessionId, marque, setHeaderAction }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);
  useEffect(() => { setData(null); api.calendrier(concessionId, marque).then((r) => setData(r.data)).catch((e) => alert(erreurDe(e))); }, [concessionId, marque]);
  if (!data) return <div className="empty-state">Chargement…</div>;
  const mois = [...new Set([...Object.keys(data.arrivees), ...Object.keys(data.paiements)])].sort();
  const modeles = [...new Set(Object.values(data.arrivees).flatMap((a) => Object.keys(a.par_modele)))].sort();
  const maxA = Math.max(1, ...Object.values(data.arrivees).map((a) => a.total));
  const maxP = Math.max(1, ...Object.values(data.paiements).map((p) => p.montant));
  const ymNow = aujourdhui().slice(0, 7);
  const totalP = Object.values(data.paiements).reduce((s, p) => s + p.montant, 0);
  return (
    <>
      <div className="cvn-kpis">
        <div className="cvn-kpi"><span className="l">À arriver</span><span className="v">{nf.format(Object.values(data.arrivees).reduce((s, a) => s + a.total, 0) + data.sans_eta.vehicules)}</span><span className="d">{data.sans_eta.vehicules ? `dont ${data.sans_eta.vehicules} sans ETA` : 'tous datés'}</span></div>
        <div className="cvn-kpi"><span className="l">À décaisser</span><span className="v">{keur(totalP + data.sans_date_paiement.montant)}</span><span className="d">{data.sans_date_paiement.lots ? `dont ${keur(data.sans_date_paiement.montant)} sans date` : 'tous datés'}</span></div>
      </div>
      <p className="cvn-regle">Lots non livrés (arrivées) et non payés (décaissements), mois par mois. Un mois passé = retard.</p>
      <div className="table-container cvn-scroll">
        <table className="cvn-table cvn-cal">
          <thead>
            <tr><th className="l">Mois</th>{modeles.map((m) => <th key={m}>{m}</th>)}<th>Arrivées</th><th className="cvn-cal-barre" /><th>Décaissements HT</th><th className="cvn-cal-barre" /></tr>
          </thead>
          <tbody>
            {mois.map((ym) => {
              const a = data.arrivees[ym]; const p = data.paiements[ym];
              return (
                <tr key={ym} className={ym < ymNow ? 'cvn-retard' : ym === ymNow ? 'cvn-courant' : ''}>
                  <td className="l">{moisLib(ym)}{ym < ymNow && <span className="badge-pill badge-error cvn-badge-ligne">en retard</span>}</td>
                  {modeles.map((m) => <td key={m}>{a?.par_modele[m] || ''}</td>)}
                  <td className="cvn-fort">{a?.total || ''}</td>
                  <td className="cvn-cal-barre"><span className="arr" style={{ width: `${((a?.total || 0) / maxA) * 100}%` }} /></td>
                  <td className="cvn-fort">{p ? keur(p.montant) : ''}{p?.sans_prix > 0 && <span className="inline-tag inline-tag-muted" title="Lots sans prix unitaire : montant incomplet">{p.sans_prix} sans prix</span>}</td>
                  <td className="cvn-cal-barre"><span className="pay" style={{ width: `${((p?.montant || 0) / maxP) * 100}%` }} /></td>
                </tr>
              );
            })}
            {mois.length === 0 && <tr><td colSpan={modeles.length + 5} className="empty-state">Rien à arriver ni à payer.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PARAMÈTRES
// ═══════════════════════════════════════════════════════════════════════════
function Parametres({ concessionId, marque, recherche, filtre, setHeaderAction }) {
  const [couv, setCouv] = useState(null);
  const [corr, setCorr] = useState(null);
  const [p, setP] = useState(null);
  const [nouvelle, setNouvelle] = useState(null);

  const charger = useCallback(() => {
    api.couverture(concessionId, marque).then((r) => { setCouv(r.data); setP(r.data.parametres); }).catch((e) => alert(erreurDe(e)));
    api.correspondances(concessionId, marque).then((r) => setCorr(r.data.correspondances)).catch(() => setCorr([]));
  }, [concessionId, marque]);
  useEffect(() => { charger(); }, [charger]);

  const nbProposees = (corr || []).filter((k) => k.propose).length;
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: { label: '+ Version', onClick: () => setNouvelle({ modele: '', libelle: '' }) },
      secondary: nbProposees ? [{ label: `✓ Confirmer les ${nbProposees} propositions`, onClick: async () => { try { await api.confirmerPropositions(concessionId, marque); charger(); } catch (e) { alert(erreurDe(e)); } } }] : [],
    });
  }, [setHeaderAction, nbProposees, concessionId, marque, charger]);

  if (!couv || !p) return <div className="empty-state">Chargement…</div>;
  const enregistrer = async () => {
    try { await api.enregistrerParametres(concessionId, marque, p); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const majVersion = async (v, champs) => { try { await api.modifierVersion(v.id, champs); charger(); } catch (e) { alert(erreurDe(e)); } };
  const classer = async (k, valeur) => {
    try { await api.classer(k.id, valeur === 'ignore' ? { ignore: true } : { version_id: valeur ? Number(valeur) : null }); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const ajouterVersion = async () => {
    try { await api.ajouterVersion(concessionId, marque, nouvelle); setNouvelle(null); charger(); } catch (e) { alert(erreurDe(e)); }
  };
  const activite = new Map((couv.a_classer || []).map((a) => [a.correspondance_id, a]));
  const libVersion = new Map(couv.versions.map((v) => [v.id, v.libelle]));
  const aTraiter = (k) => (!k.version_id && !k.ignore) || k.propose;
  const listees = (corr || []).filter((k) => (filtre === 'tout' || aTraiter(k)) && contient(recherche, k.serie, k.modele_dms, libVersion.get(k.version_id)));
  const versionsListees = couv.versions.filter((v) => contient(recherche, v.modele, v.libelle));

  return (
    <>
      <h3 className="cvn-section">Délais de {marque}</h3>
      <div className="cvn-bloc">
        <div className="form-grid cvn-delais">
          <div className="form-group"><label>Délai de commande (mois)</label><input type="number" step="0.5" min="0" value={p.delai_commande_mois} onChange={(e) => setP({ ...p, delai_commande_mois: e.target.value })} /><small>de la commande à l'arrivée chez nous</small></div>
          <div className="form-group"><label>Couverture voulue (mois)</label><input type="number" step="0.5" min="0" value={p.couverture_cible_mois} onChange={(e) => setP({ ...p, couverture_cible_mois: e.target.value })} /><small>stock de sécurité, au-delà du délai</small></div>
          <div className="form-group"><label>Délai de paiement (jours)</label><input type="number" min="0" value={p.delai_paiement_jours} onChange={(e) => setP({ ...p, delai_paiement_jours: e.target.value })} /><small>ETA + ce délai = paiement prévu</small></div>
          <div className="form-group cvn-form-actions"><button type="button" className="btn-primary" onClick={enregistrer}>Enregistrer</button></div>
        </div>
        <p className="cvn-regle">Une version peut avoir son propre délai de commande (colonne « Délai propre » des versions suivies) : il remplace celui de la marque.</p>
      </div>

      <h3 className="cvn-section">Correspondance des libellés du DMS</h3>
      <p className="cvn-regle">Le DMS nomme les versions à sa façon (« GL 1,2 5MT 2WD », « 2024 SWIFF 1.2 5MT GL »…). Chaque libellé est rattaché UNE fois à une version de suivi ; les nouveaux apparaissent ici. Les propositions automatiques comptent déjà dans la couverture : vérifiez-les.</p>
      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead><tr><th className="l">Série</th><th className="l">Libellé DMS</th><th>Ventes 24 m</th><th>Stock</th><th className="l">Version de suivi</th></tr></thead>
          <tbody>
            {listees.map((k) => {
              const a = activite.get(k.id);
              return (
                <tr key={k.id} className={k.propose ? 'cvn-propose' : (!k.version_id && !k.ignore ? 'cvn-aclasser' : '')}>
                  <td className="l">{k.serie || '—'}</td>
                  <td className="l font-mono">{k.modele_dms || '—'}</td>
                  <td>{a ? a.ventes_24m : ''}</td>
                  <td>{a ? a.stock : ''}</td>
                  <td className="l">
                    <select value={k.ignore ? 'ignore' : (k.version_id || '')} onChange={(e) => classer(k, e.target.value)}>
                      <option value="">— à classer —</option>
                      {couv.versions.map((v) => <option key={v.id} value={v.id}>{v.modele} · {v.libelle}</option>)}
                      <option value="ignore">Ignorer (hors suivi)</option>
                    </select>
                    {k.propose && <span className="badge-pill badge-info cvn-badge-ligne">proposée</span>}
                  </td>
                </tr>
              );
            })}
            {listees.length === 0 && <tr><td colSpan={5} className="empty-state">{filtre === 'tout' ? 'Aucun libellé.' : 'Tous les libellés sont rattachés.'}</td></tr>}
          </tbody>
        </table>
      </div>

      <h3 className="cvn-section">Versions suivies</h3>
      <div className="table-container cvn-scroll">
        <table className="cvn-table">
          <thead><tr><th className="l">Modèle</th><th className="l">Libellé</th><th title="Remplace le délai de commande de la marque">Délai propre (mois)</th><th>Prix d'achat HT</th><th className="l">Suivie</th></tr></thead>
          <tbody>
            {versionsListees.map((v) => (
              <tr key={v.id} className={!v.actif ? 'cvn-livre' : ''}>
                <td className="l">{v.modele}</td>
                <td className="l"><input className="cvn-libelle" defaultValue={v.libelle} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== v.libelle && majVersion(v, { libelle: e.target.value })} /></td>
                <td><input className="cvn-num" type="number" step="0.5" min="0" defaultValue={v.delai_propre ? v.delai_commande_mois : ''} placeholder={String(p.delai_commande_mois)} onBlur={(e) => e.target.value !== (v.delai_propre ? String(v.delai_commande_mois) : '') && majVersion(v, { delai_commande_mois: e.target.value })} /></td>
                <td><input className="cvn-prix" type="number" min="0" step="100" defaultValue={v.prix_achat_ht ?? ''} onBlur={(e) => String(e.target.value) !== String(v.prix_achat_ht ?? '') && majVersion(v, { prix_achat_ht: e.target.value })} /></td>
                <td className="l"><input type="checkbox" checked={v.actif} onChange={(e) => majVersion(v, { actif: e.target.checked })} /></td>
              </tr>
            ))}
            {versionsListees.length === 0 && <tr><td colSpan={5} className="empty-state">Aucune version.</td></tr>}
          </tbody>
        </table>
      </div>

      {nouvelle && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle version {marque}</h2>
              <button type="button" className="modal-close" onClick={() => setNouvelle(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Modèle *</label><input value={nouvelle.modele} autoFocus placeholder="SWIFT" onChange={(e) => setNouvelle({ ...nouvelle, modele: e.target.value })} /></div>
                <div className="form-group"><label>Libellé *</label><input value={nouvelle.libelle} placeholder="Swift 1,2 GL Hybride" onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setNouvelle(null)}>Annuler</button>
              <button type="button" className="btn-primary" disabled={!nouvelle.modele.trim() || !nouvelle.libelle.trim()} onClick={ajouterVersion}>Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
